import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CkbLogo from './CkbLogo';

const navigationGroups = [
  {
    name: 'Overview',
    icon: '📊',
    items: [
      { name: 'Dashboard', href: '/', icon: '🏠' },
    ]
  },
  {
    name: 'Token Analytics',
    icon: '🪙',
    items: [
      { name: 'Tokens', href: '/tokens', icon: '📋' },
      { name: 'Token Aggregator', href: '/aggregator', icon: '🔄' },
      { name: 'Milestones', href: '/milestones', icon: '🎯' },
      { name: 'Recovery', href: '/recovery', icon: '📈' },
      { name: 'Lifecycle / Graveyard', href: '/lifecycle', icon: '🪦' },
      { name: 'Alerts', href: '/alerts', icon: '🔔' },
      { name: 'Wallet Monitor', href: '/wallet-monitor', icon: '👛' },
      { name: 'Frontrun', href: '/frontrun', icon: '⚡' },
      { name: 'Pings', href: '/pings', icon: '📡' },
    ]
  },
  {
    name: 'Kitchen',
    icon: '👨‍🍳',
    items: [
      { name: 'Master Chef', href: '/trading/masterchef', icon: '🍳' },
      { name: 'Strategies', href: '/trading/user-strategies', icon: '📐' },
      { name: 'Positions', href: '/trading/masterchef/positions', icon: '📋' },
      { name: 'Staged Recovering', href: '/trading/masterchef/staged', icon: '🍲' },
      { name: 'Activity', href: '/trading/masterchef/activity', icon: '📜' },
      { name: 'EMA Watchlist', href: '/trading/masterchef/ema', icon: '📉' },
      { name: 'Settings', href: '/trading/masterchef/settings', icon: '🔧' },
      { name: 'Volume Monitor', href: '/trading/volume-monitor', icon: '📊' },
      { name: 'Wallet Management', href: '/trading/wallets', icon: '💼' },
    ]
  },
  {
    name: 'DataSets',
    icon: '🧪',
    items: [
      { name: 'Chef Dataset', href: '/datasets/masterchef', icon: '🍳' },
      { name: 'Entry Logs', href: '/datasets/entry-logs', icon: '📝' },
    ]
  },
  {
    name: 'Account Manager',
    icon: '👤',
    items: [
      { name: 'Users', href: '/users', icon: '👥' },
      { name: 'Groups', href: '/groups', icon: '👨‍👩‍👧‍👦' },
    ]
  },
  {
    name: 'Finance',
    icon: '💰',
    items: [
      { name: 'Subscriptions', href: '/subscriptions', icon: '⭐' },
      { name: 'Transactions', href: '/transactions', icon: '📝' },
    ]
  },
  {
    name: 'System',
    icon: '⚙️',
    items: [
      { name: 'Settings', href: '/settings', icon: '🔧' },
      { name: 'API', href: '/system/api', icon: '🔌' },
    ]
  },
];

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Auto-expand the group containing the current path
    const expanded = {};
    navigationGroups.forEach((group, index) => {
      const hasActiveItem = group.items.some(item =>
        location.pathname === item.href ||
        (item.href !== '/' && location.pathname.startsWith(item.href))
      );
      expanded[index] = hasActiveItem || index === 0; // Always expand Overview
    });
    return expanded;
  });

  const toggleGroup = (index) => {
    setExpandedGroups(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="flex flex-col w-64 bg-gray-900 min-h-screen">
      <div className="flex items-center justify-center h-16 bg-gray-800">
        <CkbLogo size={36} showText textClassName="text-white text-xl font-bold" />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigationGroups.map((group, groupIndex) => {
          const isExpanded = expandedGroups[groupIndex];
          const hasActiveItem = group.items.some(item =>
            location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href))
          );

          return (
            <div key={group.name} className="mb-2">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(groupIndex)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasActiveItem
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <div className="flex items-center">
                  <span className="mr-2 text-base">{group.icon}</span>
                  <span>{group.name}</span>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Group Items */}
              {isExpanded && (
                <div className="mt-1 ml-4 space-y-1">
                  {group.items.map((item) => {
                    const isActive = item.exact
                      ? location.pathname === item.href
                      : location.pathname === item.href
                        || location.pathname.startsWith(`${item.href}/`);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        <span className="mr-2 text-sm">{item.icon}</span>
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="p-4 border-t border-gray-800">
        <div className="flex items-center mb-3">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
            {user?.username?.[0]?.toUpperCase()}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-white">{user?.username}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full px-4 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
        >
          Logout
        </button>
      </div>
    </div>
  );
}
